import React, { useState, useEffect, useRef } from 'react';
import { CASE_STUDIES, BRAND } from '../data/mockData';
import { ArrowRight, ArrowLeft, ExternalLink, X, Search } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { api } from '../api';
import { useGoToContact } from '../hooks/useGoToContact';

const INDUSTRIES = ['All', 'Fintech', 'Entertainment', 'Gaming', 'Electronics', 'Retail', 'Healthcare', 'Beauty / FMCG', 'Digital Services', 'Education', 'Social Commerce', 'Education / Career', 'Retail / Fintech'];

const WorkPage = () => {
  const [caseStudies, setCaseStudies] = useState(CASE_STUDIES);
  const [activeFilter, setActiveFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCase, setSelectedCase] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const goToContact = useGoToContact();
  const detailRef = useRef(null);

  useEffect(() => {
    api.getCaseStudies().then(data => {
      if (data && data.length > 0) setCaseStudies(data);
    }).catch(() => {});
  }, []);

  // Scroll to specific case study if hash is present
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      const found = caseStudies.find(cs => cs.id === id);
      if (found) {
        setSelectedCase(found);
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 200);
      }
    }
  }, [location.hash, caseStudies]);

  const filtered = (() => {
    let list = activeFilter === 'All'
      ? caseStudies
      : caseStudies.filter(cs => cs.industry === activeFilter);
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      list = list.filter(cs =>
        cs.title.toLowerCase().includes(q) ||
        cs.client.toLowerCase().includes(q) ||
        (cs.industry || '').toLowerCase().includes(q) ||
        (cs.description || '').toLowerCase().includes(q) ||
        (cs.category || '').toLowerCase().includes(q)
      );
    }
    return list;
  })();

  const usedIndustries = ['All', ...new Set(caseStudies.map(cs => cs.industry))];

  return (
    <div className="bg-[#FDFEFA] min-h-screen">
      {/* Header */}
      <div className="bg-[#242326] pt-24 pb-16 md:pb-24">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 text-white/50 text-sm font-['Inter'] mb-8 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </button>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-white text-4xl md:text-6xl lg:text-[80px] font-light leading-[1.1] tracking-tight font-['Inter'] mb-4"
          >
            Our Work
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-white/60 text-lg md:text-xl font-['Lato'] font-light max-w-2xl"
          >
            Demonstrating growth through data-driven creativity and precision execution across diverse market sectors.
          </motion.p>
        </div>
      </div>

      {/* Case Study Detail Modal */}
      <AnimatePresence>
        {selectedCase && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm overflow-y-auto"
            onClick={() => setSelectedCase(null)}
          >
            <motion.div
              ref={detailRef}
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 40 }}
              transition={{ duration: 0.4 }}
              className="max-w-5xl mx-auto my-8 md:my-16 bg-white rounded-2xl overflow-hidden shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Hero */}
              <div className="relative h-[300px] md:h-[400px] overflow-hidden">
                <img
                  src={selectedCase.heroImage}
                  alt={selectedCase.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                <button
                  onClick={() => setSelectedCase(null)}
                  className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/20 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
                <div className="absolute bottom-6 left-6 md:left-10">
                  <span className="text-[#83BF42] text-xs uppercase tracking-wider font-['Inter'] bg-white/10 backdrop-blur-sm px-3 py-1 rounded-full">
                    {selectedCase.category}
                  </span>
                  <h2 className="text-white text-2xl md:text-4xl font-light font-['Inter'] mt-3 max-w-2xl leading-tight">
                    {selectedCase.title}
                  </h2>
                </div>
              </div>

              {/* Modal Body */}
              <div className="p-6 md:p-10 lg:p-12">
                {/* Client & Industry */}
                <div className="flex flex-wrap gap-6 mb-8 pb-8 border-b border-[#242326]/8">
                  <div>
                    <p className="text-[#B8B8B8] text-xs uppercase tracking-wider font-['Inter'] mb-1">Client</p>
                    <p className="text-[#242326] text-lg font-medium font-['Inter']">{selectedCase.client}</p>
                  </div>
                  <div>
                    <p className="text-[#B8B8B8] text-xs uppercase tracking-wider font-['Inter'] mb-1">Industry</p>
                    <p className="text-[#242326] text-lg font-medium font-['Inter']">{selectedCase.industry}</p>
                  </div>
                  <div>
                    <p className="text-[#B8B8B8] text-xs uppercase tracking-wider font-['Inter'] mb-1">Platform</p>
                    <p className="text-[#242326] text-lg font-medium font-['Inter']">{selectedCase.platform}</p>
                  </div>
                </div>

                {/* Stats Highlight */}
                <div className="bg-[#242326] rounded-xl p-6 md:p-8 mb-8">
                  <div className="text-[#83BF42] text-4xl md:text-6xl font-bold font-['Inter'] mb-2">
                    {selectedCase.statHighlight}
                  </div>
                  <div className="text-white/60 text-sm font-['Lato'] uppercase tracking-wider">
                    {selectedCase.statLabel}
                  </div>
                </div>

                {/* What Was Done */}
                <div className="mb-8">
                  <h3 className="text-[#242326] text-lg font-medium font-['Inter'] mb-3 uppercase tracking-wider text-sm">What Was Done</h3>
                  <p className="text-[#4A4A48] text-base md:text-lg leading-relaxed font-['Lato'] font-light">
                    {selectedCase.whatWasDone}
                  </p>
                </div>

                {/* Dashboard Screenshots */}
                {selectedCase.dashboardImage && (
                  <div className="mb-8">
                    <h3 className="text-[#242326] text-sm font-medium font-['Inter'] mb-4 uppercase tracking-wider">Campaign Dashboard</h3>
                    <div className={`grid gap-4 ${selectedCase.dashboardImage2 ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'}`}>
                      <div className="rounded-xl overflow-hidden border border-[#242326]/8 shadow-sm">
                        <img
                          src={selectedCase.dashboardImage}
                          alt="Campaign dashboard"
                          className="w-full h-auto"
                        />
                      </div>
                      {selectedCase.dashboardImage2 && (
                        <div className="rounded-xl overflow-hidden border border-[#242326]/8 shadow-sm">
                          <img
                            src={selectedCase.dashboardImage2}
                            alt="Campaign analytics"
                            className="w-full h-auto"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Stats Grid */}
                <div className="flex gap-8 pt-6 border-t border-[#242326]/8">
                  {selectedCase.stats.map((stat, i) => (
                    <div key={stat.label || `stat-${i}`}>
                      <div className="text-[#83BF42] text-2xl md:text-3xl font-bold font-['Inter']">
                        {stat.value}
                      </div>
                      <div className="text-[#4A4A48] text-sm font-['Lato'] mt-1">
                        {stat.label}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Get in Touch CTA */}
                <div className="mt-10 pt-8 border-t border-[#242326]/8">
                  <div className="bg-[#242326] rounded-xl p-6 md:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div>
                      <h4 className="text-white text-lg font-medium font-['Inter'] mb-1">
                        Want similar results?
                      </h4>
                      <p className="text-white/50 text-sm font-['Lato']">
                        Let's discuss how we can drive growth for your brand.
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        setSelectedCase(null);
                        goToContact();
                      }}
                      data-testid="work-modal-contact-cta"
                      className="flex-shrink-0 inline-flex items-center gap-2 px-6 py-2.5 rounded-full border border-[#83BF42] text-[#83BF42] text-sm font-['Inter'] hover:bg-[#83BF42] hover:text-white transition-all duration-300 group"
                    >
                      Get in Touch
                      <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Filters & Search */}
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 py-8 md:py-12">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex flex-wrap gap-2">
            {usedIndustries.map((industry) => (
              <button
                key={industry}
                onClick={() => setActiveFilter(industry)}
                data-testid={`work-filter-${industry.toLowerCase().replace(/[\s/]+/g, '-')}`}
                className={`px-4 py-2 rounded-full text-sm font-['Inter'] transition-all duration-300 ${
                  activeFilter === industry
                    ? 'bg-[#242326] text-white'
                    : 'bg-[#242326]/5 text-[#4A4A48] hover:bg-[#242326]/10'
                }`}
              >
                {industry}
              </button>
            ))}
          </div>

          <div className="relative w-full lg:w-80">
            <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#B8B8B8]" />
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search case studies…"
              data-testid="work-search-input"
              className="w-full pl-11 pr-10 py-2.5 rounded-full bg-[#242326]/5 text-[#242326] placeholder-[#B8B8B8] font-['Lato'] text-sm focus:outline-none focus:bg-white focus:ring-2 focus:ring-[#83BF42]/30 transition-all"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                data-testid="work-search-clear"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#B8B8B8] hover:text-[#242326]"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
        {searchQuery && (
          <p className="text-[#4A4A48] text-sm font-['Lato'] mt-4" data-testid="work-search-results-count">
            {filtered.length} {filtered.length === 1 ? 'case study' : 'case studies'} matching "{searchQuery}"
          </p>
        )}
      </div>

      {/* Case Studies Grid */}
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 pb-20">
        {filtered.length === 0 ? (
          <div className="text-center py-20" data-testid="work-empty-state">
            <p className="text-[#4A4A48] font-['Lato'] text-lg">No case studies match your filters.</p>
            <button
              onClick={() => { setSearchQuery(''); setActiveFilter('All'); }}
              className="mt-4 text-[#83BF42] text-sm font-['Inter'] hover:underline"
              data-testid="work-clear-all"
            >
              Clear filters
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((study, index) => (
            <motion.div
              key={study.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-30px' }}
              transition={{ duration: 0.5, delay: (index % 6) * 0.08 }}
              className="group cursor-pointer"
              onClick={() => {
                setSelectedCase(study);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            >
              {/* Card Image */}
              <div className="relative h-[260px] md:h-[280px] rounded-xl overflow-hidden mb-4">
                <img
                  src={study.heroImage}
                  alt={study.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

                {/* Stat Overlay */}
                <div className="absolute bottom-4 left-4">
                  <div className="text-[#83BF42] text-2xl md:text-3xl font-bold font-['Inter']">
                    {study.statHighlight}
                  </div>
                </div>

                {/* Category Tag */}
                <div className="absolute top-4 right-4">
                  <span className="text-white/90 text-xs uppercase tracking-wider font-['Inter'] bg-white/10 backdrop-blur-sm px-3 py-1 rounded-full">
                    {study.category}
                  </span>
                </div>
              </div>

              {/* Card Text */}
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[#83BF42] text-xs uppercase tracking-wider font-bold font-['Inter']">
                  {study.client}
                </span>
                <span className="text-[#B8B8B8] text-xs font-['Lato']">|</span>
                <span className="text-[#B8B8B8] text-xs font-['Lato']">
                  {study.industry}
                </span>
              </div>

              <h3 className="text-[#242326] text-lg md:text-xl font-medium font-['Inter'] leading-snug mb-2 group-hover:text-[#83BF42] transition-colors duration-300">
                {study.title}
              </h3>

              <p className="text-[#4A4A48] text-sm font-['Lato'] font-light leading-relaxed line-clamp-2">
                {study.description}
              </p>

              <div className="flex items-center gap-1 text-[#83BF42] text-sm font-['Inter'] mt-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                View details
                <ArrowRight className="w-3.5 h-3.5" />
              </div>
            </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Bottom CTA */}
      <div className="bg-[#242326] py-16 md:py-20">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 text-center">
          <h2 className="text-white text-3xl md:text-4xl lg:text-5xl font-light font-['Inter'] mb-4">
            Ready to be our next success story?
          </h2>
          <p className="text-white/50 text-lg font-['Lato'] font-light mb-8">
            Let's discuss how we can drive results for your brand.
          </p>
          <button
            onClick={goToContact}
            data-testid="work-bottom-contact-cta"
            className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full border border-[#83BF42] text-[#83BF42] text-[15px] font-['Inter'] font-light hover:bg-[#83BF42] hover:text-white transition-all duration-300 group"
          >
            Get in Touch
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default WorkPage;
